import type { Job, Issue } from "./types";
import { migrationBypasses } from "./import/migrationVisibilityGuard";

export interface Blocker {
  label: string;
  detail: string;
  severity: "critical" | "warning" | "info";
  category: "permit" | "materials" | "issues" | "qa" | "contractor" | "dispatch";
}

export function getJobBlockers(job: Job, openIssues: Issue[]): Blocker[] {
  const blockers: Blocker[] = [];

  // Permit — suppressed if permitting is bypassed
  if (!migrationBypasses(job, "permitting")) {
    if (job.permitRequired && job.permitStatus === "Revision Needed")
      blockers.push({ label: "Permit Revision Needed", detail: "City requires revised plans", severity: "critical", category: "permit" });
    if (job.permitRequired && job.permitStatus === "Submitted")
      blockers.push({ label: "Awaiting Permit Approval", detail: "Permit submitted, waiting on city", severity: "warning", category: "permit" });
  } else if (job.permitRequired) {
    blockers.push({ label: "Permitting — Bypassed", detail: "Permitting bypassed for this migrated job.", severity: "info", category: "permit" });
  }

  // Materials — suppressed if ordering is bypassed
  if (migrationBypasses(job, "ordering")) {
    blockers.push({ label: "Ordering — Bypassed", detail: "Ordering bypassed for this migrated job. Ordering can still allocate materials if needed.", severity: "info", category: "materials" });
  } else {
    if (job.materialReadiness === "Missing Items")
      blockers.push({ label: "Missing Materials", detail: "Materials unavailable or on backorder", severity: "critical", category: "materials" });
    if (job.materialReadiness === "Field Purchase Required")
      blockers.push({ label: "Field Purchase Required", detail: "Items need to be purchased in the field", severity: "warning", category: "materials" });
    if (
      job.materialReadiness === "Not Ordered" &&
      job.currentStage !== "Sale Closed" &&
      job.currentStage !== "Review and Validation"
    )
      blockers.push({ label: "Materials Not Ordered", detail: "No materials order placed yet", severity: "warning", category: "materials" });
  }

  // Issues
  if (openIssues.length > 0) {
    const hasUrgent = openIssues.some((i) => i.severity === "Urgent");
    blockers.push({
      label: `${openIssues.length} Unresolved Issue${openIssues.length > 1 ? "s" : ""}`,
      detail: "Open issues must be resolved before completion",
      severity: hasUrgent ? "critical" : "warning",
      category: "issues",
    });
  }

  // QA
  if (!job.qaSubmitted && (job.currentStage === "QA Review" || job.currentStage === "Completed"))
    blockers.push({ label: "Install QA Not Submitted", detail: "QA form required before completion", severity: "critical", category: "qa" });

  // Contractor / Dispatch
  if (job.assignmentType === "Subcontractor" && !job.assignedSubcontractor && job.currentStage !== "Sale Closed" && job.currentStage !== "Review and Validation")
    blockers.push({ label: "Subcontractor Not Assigned", detail: "No winning contractor selected yet", severity: "warning", category: "contractor" });

  if (job.dispatchStatus === "Awaiting Response" || job.dispatchStatus === "Sent to Contractor Network")
    blockers.push({ label: "Awaiting Contractor Response", detail: "Waiting on bids or acceptance", severity: "warning", category: "contractor" });

  if (job.dispatchStatus === "Rejected")
    blockers.push({ label: "Contractor Rejected", detail: "Assigned contractor rejected this job", severity: "critical", category: "contractor" });

  // Dispatch readiness
  if (
    job.assignmentType === "Unassigned" &&
    job.currentStage !== "Sale Closed" &&
    job.currentStage !== "Review and Validation" &&
    job.currentStage !== "Permit and Order Pending" &&
    job.currentStage !== "Completed"
  )
    blockers.push({ label: "No Crew Assigned", detail: "Job needs crew or subcontractor assignment", severity: "info", category: "dispatch" });

  return blockers;
}

/** Short blocker labels for inline use in tables */
export function getBlockerTags(job: Job, openIssues: Issue[]): { label: string; severity: "critical" | "warning" | "info" }[] {
  return getJobBlockers(job, openIssues).map((b) => ({ label: b.label, severity: b.severity }));
}
